import { assertFinite, FORMAT_LOCALE, FormatInputError } from "./errors";

// Four kinds of time value, never mixed (LOC-104 section 4):
// - instant: a moment (UTC/offset ISO string, epoch ms or Date), shown in an
//   explicit IANA time zone: the viewer's browser zone in the dashboard, the
//   charger or session-snapshot zone for tariff windows and receipts;
// - calendar date: "YYYY-MM-DD" without a zone, never shifted;
// - wall time: tariff/TOU "HH:MM" in the station's zone, always 12-hour
//   (product decision kept from lib/time12.ts), never converted;
// - duration: an amount of time in an explicit unit.

export type InstantInput = string | number | Date;
export type InstantPreset = "dateTime" | "date" | "time" | "dateTimeZone" | "timeZone" | "dateTimeSeconds" | "timeSeconds" | "monthDayTime";

const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,9})?)?(Z|[+-]\d{2}:\d{2})$/;

/**
 * Parses an instant. Strings must carry Z or an offset: a naive
 * "2026-10-23T14:05:00" would silently be read in the browser's zone
 * (separate defect S-4), so it is rejected rather than guessed.
 */
export function parseInstant(input: InstantInput): Date {
  let date: Date;
  if (input instanceof Date) date = new Date(input.getTime());
  else if (typeof input === "number") {
    assertFinite(input, "epoch milliseconds");
    date = new Date(input);
  } else if (typeof input === "string" && ISO_INSTANT.test(input)) date = new Date(input);
  else throw new FormatInputError("instant", `${JSON.stringify(input)} is not an instant with Z or an offset`);
  if (Number.isNaN(date.getTime())) throw new FormatInputError("instant", `${String(input)} is not a valid instant`);
  return date;
}

const dateTimeFormats = new Map<string, Intl.DateTimeFormat>();

function dateTimeFormat(timeZone: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${timeZone}|${JSON.stringify(options)}`;
  let format = dateTimeFormats.get(key);
  if (!format) {
    try {
      format = new Intl.DateTimeFormat(FORMAT_LOCALE, { ...options, timeZone });
    } catch {
      throw new FormatInputError("time_zone", `${JSON.stringify(timeZone)} is not an IANA time zone`);
    }
    dateTimeFormats.set(key, format);
  }
  return format;
}

const PRESETS: Record<InstantPreset, Intl.DateTimeFormatOptions> = {
  dateTime: { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" },
  date: { year: "numeric", month: "short", day: "numeric" },
  time: { hour: "numeric", minute: "2-digit" },
  dateTimeZone: { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" },
  timeZone: { hour: "numeric", minute: "2-digit", timeZoneName: "short" },
  // Support views (telemetry frames) where seconds are the information.
  dateTimeSeconds: { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", second: "2-digit" },
  timeSeconds: { hour: "numeric", minute: "2-digit", second: "2-digit" },
  // Compact axis ticks within a short window (no year).
  monthDayTime: { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" },
};

/**
 * formatInstant("2026-10-23T18:05:00Z", { timeZone: "America/New_York" }) -> "Oct 23, 2026, 2:05 PM".
 * The time zone is required; there is no implicit browser zone.
 */
export function formatInstant(instant: InstantInput, options: { readonly timeZone: string; readonly preset?: InstantPreset }): string {
  if (typeof options?.timeZone !== "string" || options.timeZone === "") throw new FormatInputError("time_zone", "a time zone is required");
  const date = parseInstant(instant);
  return dateTimeFormat(options.timeZone, PRESETS[options.preset ?? "dateTime"]).format(date);
}

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const CALENDAR_PRESETS = {
  medium: { year: "numeric", month: "short", day: "numeric" },
  long: { year: "numeric", month: "long", day: "numeric" },
  numeric: { year: "numeric", month: "2-digit", day: "2-digit" },
} satisfies Record<string, Intl.DateTimeFormatOptions>;

/** formatCalendarDate("2026-10-23") -> "Oct 23, 2026", identical in every time zone. */
export function formatCalendarDate(date: string, preset: keyof typeof CALENDAR_PRESETS = "medium"): string {
  const m = typeof date === "string" ? CALENDAR_DATE.exec(date) : null;
  const [y, mo, d] = m ? [Number(m[1]), Number(m[2]), Number(m[3])] : [0, 0, 0];
  const utc = new Date(Date.UTC(y, mo - 1, d));
  if (!m || utc.getUTCFullYear() !== y || utc.getUTCMonth() !== mo - 1 || utc.getUTCDate() !== d) {
    throw new FormatInputError("calendar_date", `${JSON.stringify(date)} is not a YYYY-MM-DD date`);
  }
  return dateTimeFormat("UTC", CALENDAR_PRESETS[preset]).format(utc);
}

const WALL_TIME = /^(\d{1,2}):(\d{2})$/;

/**
 * Tariff wall-clock "HH:MM" (24-hour wire format) as 12-hour text:
 * "14:30" -> "2:30 PM", "00:00" and the end-of-day "24:00" -> "12:00 AM".
 * Always 12-hour regardless of locale, with a plain space, as lib/time12.ts.
 */
export function formatWallTime(hhmm: string): string {
  const m = typeof hhmm === "string" ? WALL_TIME.exec(hhmm.trim()) : null;
  const hour = m ? Number(m[1]) : NaN;
  const minute = m ? Number(m[2]) : NaN;
  if (!m || hour > 24 || minute > 59 || (hour === 24 && minute !== 0)) {
    throw new FormatInputError("wall_time", `${JSON.stringify(hhmm)} is not an HH:MM wall time`);
  }
  const h = hour % 24;
  return `${h % 12 === 0 ? 12 : h % 12}:${m[2]} ${h < 12 ? "AM" : "PM"}`;
}

export type DurationUnit = "milliseconds" | "seconds" | "minutes";
const UNIT_SECONDS: Record<DurationUnit, number> = { milliseconds: 0.001, seconds: 1, minutes: 60 };

/**
 * Durations in an explicit unit, rounded to whole seconds.
 * compact: "2h 5m", "5m 12s", "12s" (dashboard style); clock: "2:05:12", "5:12".
 */
export function formatDuration(value: number, unit: DurationUnit, style: "compact" | "clock" = "compact"): string {
  if (!(unit in UNIT_SECONDS)) throw new FormatInputError("unit", String(unit));
  assertFinite(value, "duration");
  if (value < 0) throw new FormatInputError("negative", `duration ${value}`);
  const total = Math.round(value * UNIT_SECONDS[unit]);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (style === "clock") {
    const ss = String(seconds).padStart(2, "0");
    return hours ? `${hours}:${String(minutes).padStart(2, "0")}:${ss}` : `${minutes}:${ss}`;
  }
  if (hours) return `${hours}h ${minutes}m`;
  if (minutes) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}
